/**
 * O RPG Desk como anfitrião de uma página de plugin web (docs/spec/web-plugins.md, "Conexão").
 *
 * Um `WebHost` por iframe:
 * - responde ao primeiro `rpgdesk:hello` do iframe dele, com uma `MessagePort`; um segundo `hello` ou
 *   um segundo `load` é a página navegando para outro lugar, e o iframe é descartado;
 * - manda o documento (retrato e mudanças da réplica da mesa) e traduz as escritas da página
 *   (intenções) em operações do NDB, conferidas aqui e de novo no servidor;
 * - atende os pedidos (rolagem, chat, diálogos, arquivos…) e manda um sinal a cada 5 s, para
 *   perceber a página travada.
 *
 * A página não é confiável: tudo o que vem dela é conferido.
 */
import { DESK_FILE_REF, MEMBER_LIMITS, type LibraryItem, type RoomPlugin } from '../../shared.js';
import type { ReplicaNodeDatabase, TreeEvent } from '../../ndb.js';
import { useApp } from '../../store.js';
import { useLanguage } from '../../i18n/index.js';
import { avatarSrc } from '../../social/avatarUrl.js';
import { fileUrl } from '../../files.js';
import { currentNdbIdentity, onDocReject, openDoc, releaseDoc } from '../ndbClient.js';
import { PluginDrag } from './pluginDrag.js';
import {
  IntentError,
  PROTOCOL,
  privateViolation,
  resolveIntents,
  type AttrChange,
  type InitEvent,
  type Intent,
  type LibraryEntry,
  type MenuItem,
  type Methods,
  type Player,
  type PluginContext,
  type RequestMessage,
  type ResponseMessage,
  type RollResult,
  type Writer,
} from './sdk.js';
import { themeVars } from './theme.js';

export type HostState = 'loading' | 'ready' | 'hung' | 'navigated' | 'timeout';
export type PageError = { message: string; stack?: string; at: number };

/** O que a interface oferece ao anfitrião (diálogos e menus são desenhados pelo RPG Desk). */
export type HostUi = {
  confirm(opts: { title: string; message: string }): Promise<boolean>;
  prompt(opts: { title: string; label: string; initial: string }): Promise<string | null>;
  menu(items: MenuItem[], at: { x: number; y: number }): Promise<string | null>;
  pickImage(): Promise<string | null>;
  toast(text: string, kind: 'info' | 'success' | 'error'): void;
  openLink(url: string): Promise<boolean>;
};

export type WebHostOptions = {
  iframe: HTMLIFrameElement;
  plugin: RoomPlugin;
  item: LibraryItem;
  kind: 'sheet' | 'table';
  ui: HostUi;
  onState: (state: HostState) => void;
  onError: (error: PageError) => void;
};

const PING_MS = 5000;
const HUNG_AFTER = 3;
const HELLO_TIMEOUT_MS = 10_000;
const STORAGE_LIMIT = 1024 * 1024;
/** Teto da vista de uma aba (`RPGDesk.view`), em caracteres do JSON guardado. */
const VIEW_LIMIT = 100_000;

/** Balde de fichas: `n` por `ms` (rolagens e mensagens de uma página). */
class Bucket {
  private stamps: number[] = [];
  constructor(
    private readonly n: number,
    private readonly ms: number,
  ) {}
  take(): boolean {
    const now = Date.now();
    this.stamps = this.stamps.filter((t) => now - t < this.ms);
    if (this.stamps.length >= this.n) return false;
    this.stamps.push(now);
    return true;
  }
}

/** O erro que volta para a página do plugin, na língua da interface. */
const say = (pt: string, en: string): string => (useLanguage.getState().language === 'en' ? en : pt);

class HostError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const viewKey = (itemId: string) => `rpgdesk.webview.${itemId}`;

/** Id de arquivo do Desk Cloud vindo da página (com ou sem o prefixo `rpgdesk-file:`), ou null. */
export function pageFileId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const id = value.startsWith(DESK_FILE_REF) ? value.slice(DESK_FILE_REF.length) : value;
  return /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null;
}

/**
 * O texto de `chat.send` com a imagem do Desk Cloud embutida (`[§I rpgdesk-file:<id>]`), dentro do
 * teto do chat: o texto encolhe, a imagem fica. Quem pode ver confere o servidor (desk-cloud.md).
 */
export function withImage(body: string, image: unknown): string {
  if (image === undefined || image === null || image === '') return body;
  const id = pageFileId(image);
  if (!id) throw new HostError('bad-request', say('imagem inválida: use o id de um arquivo do Desk Cloud', 'invalid image: use the id of a Desk Cloud file'));
  const code = `[§I ${DESK_FILE_REF}${id}]`;
  if (!body) return code;
  return `${body.slice(0, 2000 - code.length - 1)}\n${code}`;
}

function readView(itemId: string): Record<string, unknown> {
  try {
    const raw = window.sessionStorage.getItem(viewKey(itemId));
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function roleOf(modes: readonly string[]): 'gm' | 'player' | 'spectator' {
  return modes.includes('gm') ? 'gm' : modes.includes('player') ? 'player' : 'spectator';
}

export class WebHost {
  private port: MessagePort | null = null;
  private db: ReplicaNodeDatabase | null = null;
  private dbPromise: Promise<ReplicaNodeDatabase> | null = null;
  private loads = 0;
  private disposed = false;
  private readonly stops: (() => void)[] = [];
  private pingN = 0;
  private missed = 0;
  private state: HostState = 'loading';
  private readonly rolls = new Bucket(10, 10_000);
  private readonly messages = new Bucket(10, 10_000);
  private readonly hooks = new Bucket(20, 60_000);
  private view: Record<string, unknown>;
  private lastPlayers = '';
  private lastLibrary = '';
  private lastWriter = '';
  private lastMe = '';
  /** a imagem que a página está arrastando para o chat */
  private drag: PluginDrag | null = null;

  constructor(private opts: WebHostOptions) {
    this.view = readView(opts.item.id);
  }

  /** O item mudou (nome, dono): o que a página vê de quem é dono acompanha. */
  updateItem(item: LibraryItem): void {
    if (item.id !== this.opts.item.id) return;
    this.opts = { ...this.opts, item };
    const me = JSON.stringify(this.me());
    if (this.port && me !== this.lastMe) {
      this.lastMe = me;
      this.event('me', { me: JSON.parse(me) });
    }
  }

  start(): void {
    const { iframe, item } = this.opts;
    this.dbPromise = openDoc(item.id).then((db) => (this.db = db));
    const onMessage = (e: MessageEvent) => {
      if (e.source !== iframe.contentWindow || this.disposed) return;
      const data = e.data as { type?: string; protocol?: number } | null;
      if (!data || data.type !== 'rpgdesk:hello') return;
      // um segundo hello: outra página no mesmo iframe
      if (this.port) return this.navigated();
      void this.handshake(e.source as Window);
    };
    window.addEventListener('message', onMessage);
    this.stops.push(() => window.removeEventListener('message', onMessage));
    // antes do `hello` vêm o about:blank do iframe novo e nada mais; depois dele, o primeiro `load`
    // é o da própria página, e um segundo é outra página no lugar dela
    const onLoad = () => {
      if (!this.port) return;
      this.loads += 1;
      if (this.loads > 1) this.navigated();
    };
    iframe.addEventListener('load', onLoad);
    this.stops.push(() => iframe.removeEventListener('load', onLoad));
    const timer = setTimeout(() => {
      if (!this.port && !this.disposed) this.setState('timeout');
    }, HELLO_TIMEOUT_MS);
    this.stops.push(() => clearTimeout(timer));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.drag?.cancel();
    this.drag = null;
    for (const stop of this.stops.splice(0)) stop();
    this.port?.close();
    this.port = null;
    if (this.dbPromise) releaseDoc(this.opts.item.id);
  }

  private setState(state: HostState): void {
    if (this.state === state) return;
    this.state = state;
    this.opts.onState(state);
  }

  private navigated(): void {
    this.setState('navigated');
    this.dispose();
    // a página que entrou no lugar não fica rodando atrás do aviso
    this.opts.iframe.src = 'about:blank';
  }

  private send(message: ResponseMessage | { ev: string; data: unknown }): void {
    this.port?.postMessage(message);
  }

  private event(ev: string, data: unknown): void {
    this.send({ ev, data });
  }

  /* ---------------------------------------------------------------- contexto */

  private writer(): Writer {
    const id = currentNdbIdentity();
    return id ? { login: id.who.login, groups: [...id.who.groups], owner: id.owner } : { login: '', groups: [], owner: false };
  }

  private me(): PluginContext['me'] {
    const { user, room, members } = useApp.getState();
    const role = room && user && room.ownerId === user.id ? 'gm' : roleOf(members[user?.id ?? '']?.modes ?? []);
    return { id: user?.id ?? '', nick: members[user?.id ?? '']?.nick ?? user?.displayName ?? '', role, isOwner: !!user && this.opts.item.ownerId === user.id };
  }

  private players(): Player[] {
    const { users, members, room } = useApp.getState();
    return users.map((u) => {
      const m = members[u.id];
      const photo = avatarSrc(u.id, u.avatarVersion, room ? { id: room.id, avatarVersion: m?.avatarVersion ?? 0 } : undefined);
      const role = room?.ownerId === u.id ? 'gm' : roleOf(m?.modes ?? []);
      return { id: u.id, nick: m?.nick ?? u.displayName, role, avatar: photo ? new URL(photo, location.origin).href : '', mainCharacterId: m?.mainCharacterId ?? null };
    });
  }

  private library(): LibraryEntry[] {
    return useApp
      .getState()
      .library.filter((i) => i.kind !== 'dock' && i.kind !== 'macros')
      .map((i) => ({ id: i.id, name: i.name, kind: i.kind, type: i.dataType, ownerId: i.ownerId, parentId: i.parentId }));
  }

  private privateParts(): Record<string, 'gm' | 'owner'> {
    return this.opts.plugin.dataTypes.find((d) => d.id === this.opts.item.dataType)?.private ?? {};
  }

  private async handshake(source: Window): Promise<void> {
    const channel = new MessageChannel();
    this.port = channel.port1;
    source.postMessage({ type: 'rpgdesk:init', protocol: PROTOCOL }, '*', [channel.port2]);
    const db = await this.dbPromise!;
    if (this.disposed) return;
    const { room } = useApp.getState();
    const init: InitEvent = {
      context: {
        kind: this.opts.kind,
        item: { id: this.opts.item.id, name: this.opts.item.name, type: this.opts.item.dataType ?? '', ownerId: this.opts.item.ownerId },
        me: this.me(),
        room: { id: room?.id ?? '', name: room?.name ?? '', system: room?.rpgSystem ?? '' },
        language: useLanguage.getState().language === 'en' ? 'en' : 'pt',
        test: false,
        plugin: { id: this.opts.plugin.moduleId, version: this.opts.plugin.version },
      },
      writer: this.writer(),
      snapshot: db.visible().snapshot(),
      private: this.privateParts(),
      theme: themeVars(),
      view: this.view,
      players: this.players(),
    };
    this.port.onmessage = (e: MessageEvent) => void this.receive(e.data as RequestMessage & { ev?: string; data?: unknown });
    this.port.start();
    this.event('init', init);
    this.lastPlayers = JSON.stringify(init.players);
    this.lastWriter = JSON.stringify(init.writer);
    this.lastMe = JSON.stringify(init.context.me);
    this.lastLibrary = JSON.stringify(this.library());

    // o documento: atributos vão como mudança; o resto (nós, permissões) como retrato
    this.stops.push(
      db.subscribe((events: TreeEvent[], local: boolean) => {
        if (!this.port) return;
        if (events.every((e) => e.type === 'changed')) {
          const changes: AttrChange[] = events.map((e) => {
            const c = e as Extract<TreeEvent, { type: 'changed' }>;
            return { node: c.node, name: c.attribute, value: c.newValue ?? null };
          });
          if (changes.length) this.event('doc.changes', { changes, local });
        } else {
          this.event('doc.snapshot', { snapshot: db.visible().snapshot(), local });
        }
      }),
    );
    this.stops.push(onDocReject(this.opts.item.id, (error) => this.event('doc.rejected', { message: error })));
    // papel, jogadores e biblioteca mudam com a mesa
    this.stops.push(
      useApp.subscribe(() => {
        const writer = JSON.stringify(this.writer());
        if (writer !== this.lastWriter) {
          this.lastWriter = writer;
          this.event('writer', JSON.parse(writer));
        }
        const me = JSON.stringify(this.me());
        if (me !== this.lastMe) {
          this.lastMe = me;
          this.event('me', { me: JSON.parse(me) });
        }
        const players = JSON.stringify(this.players());
        if (players !== this.lastPlayers) {
          this.lastPlayers = players;
          this.event('players', { players: JSON.parse(players) });
        }
        const library = JSON.stringify(this.library());
        if (library !== this.lastLibrary) {
          this.lastLibrary = library;
          this.event('library', { items: JSON.parse(library) });
        }
      }),
    );
    this.stops.push(useLanguage.subscribe((s) => this.event('language', { language: s.language === 'en' ? 'en' : 'pt' })));
    const ping = setInterval(() => {
      this.missed += 1;
      if (this.missed > HUNG_AFTER) this.setState('hung');
      this.event('ping', { n: ++this.pingN });
    }, PING_MS);
    this.stops.push(() => clearInterval(ping));
  }

  /* ---------------------------------------------------------------- vindo da página */

  private async receive(msg: RequestMessage & { ev?: string; data?: unknown }): Promise<void> {
    if (!msg || typeof msg !== 'object' || this.disposed) return;
    if (typeof msg.ev === 'string') {
      const data = (msg.data ?? {}) as Record<string, unknown>;
      switch (msg.ev) {
        case 'ready':
          this.setState('ready');
          break;
        case 'pong':
          this.missed = 0;
          if (this.state === 'hung') this.setState('ready');
          break;
        case 'error':
          this.opts.onError({ message: String(data.message ?? '').slice(0, 1000), ...(typeof data.stack === 'string' ? { stack: data.stack.slice(0, 3000) } : {}), at: Date.now() });
          break;
        case 'key':
          this.key(data);
          break;
        case 'drag.start':
        case 'drag.move':
        case 'drag.end':
        case 'drag.cancel':
          this.dragEvent(msg.ev, data);
          break;
      }
      return;
    }
    if (typeof msg.id !== 'number' || typeof msg.method !== 'string') return;
    try {
      const result = await this.answer(msg.method, (msg.params ?? {}) as never);
      this.send({ id: msg.id, ok: true, result });
    } catch (error) {
      const code = error instanceof HostError ? error.code : error instanceof IntentError ? 'rejected' : 'failed';
      const message = error instanceof IntentError ? error.localized(useLanguage.getState().language === 'en' ? 'en' : 'pt') : error instanceof Error ? error.message : String(error);
      this.send({ id: msg.id, ok: false, error: { code, message: message.slice(0, 500) } });
    }
  }

  /**
   * Arrastar uma imagem do Desk Cloud para fora da página (`data-drag`): a página manda a posição do
   * ponteiro, e `PluginDrag` desenha e solta no chat. Um arrastar por vez; o resto é ignorado.
   */
  private dragEvent(ev: string, data: Record<string, unknown>): void {
    const x = Number(data.x);
    const y = Number(data.y);
    const at = Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) < 100_000 && Math.abs(y) < 100_000;
    if (ev === 'drag.start') {
      this.drag?.cancel();
      const id = pageFileId(data.id);
      this.drag = id && at ? new PluginDrag(this.opts.iframe, id, x, y) : null;
      return;
    }
    if (!this.drag) return;
    if (ev === 'drag.move' && at) this.drag.move(x, y);
    else if (ev === 'drag.end' && at) {
      this.drag.end(x, y);
      this.drag = null;
    } else if (ev !== 'drag.move') {
      this.drag.cancel();
      this.drag = null;
    }
  }

  /**
   * Teclas que a página não tratou: o RPG Desk recebe como se fossem dele. Só as que o SDK repassa (Esc
   * e os atalhos com Ctrl/⌘): um plugin não manda Delete, setas ou letras soltas para a mesa (apagar o
   * token selecionado, mover, trocar de ferramenta).
   */
  private key(data: Record<string, unknown>): void {
    const key = String(data.key ?? '');
    if (!key || key.length > 20) return;
    if (key !== 'Escape' && !data.ctrl && !data.meta) return;
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key, code: String(data.code ?? ''), ctrlKey: !!data.ctrl, shiftKey: !!data.shift, altKey: !!data.alt, metaKey: !!data.meta, bubbles: true, cancelable: true }),
    );
  }

  private async answer<M extends keyof Methods>(method: M, params: Methods[M]['params']): Promise<unknown> {
    const p = params as Record<string, unknown>;
    const s = useApp.getState();
    const text = (v: unknown, max: number) => (typeof v === 'string' ? v : String(v ?? '')).slice(0, max);
    switch (method) {
      case 'doc.write': {
        const db = this.db;
        if (!db) throw new HostError('failed', say('o documento ainda não abriu', 'the document has not opened yet'));
        if (!Array.isArray(p.intents) || p.intents.length > 500) throw new HostError('bad-request', say('escrita inválida', 'invalid write'));
        const ops = resolveIntents(db.visible(), p.intents as Intent[], { newId: () => db.newId(), ownerId: this.opts.item.ownerId });
        if (ops.length === 0) return null;
        const id = currentNdbIdentity();
        const parts = this.privateParts();
        const violation = id && !id.owner && Object.keys(parts).length ? privateViolation(db.visible(), ops, parts, useLanguage.getState().language === 'en' ? 'en' : 'pt') : null;
        if (violation) throw new HostError('rejected', violation);
        try {
          db.write(ops);
        } catch (error) {
          throw new HostError('rejected', error instanceof Error ? error.message : String(error));
        }
        return null;
      }
      case 'dice.roll': {
        if (!this.rolls.take()) throw new HostError('rate-limited', say('rolagens demais em pouco tempo', 'too many rolls in a short time'));
        const expression = text(p.expression, 120).trim();
        if (!expression) throw new HostError('bad-request', say('rolagem vazia', 'empty roll'));
        const label = p.label ? text(p.label, 80) : null;
        const roll = await s.rollForPlugin(expression, label);
        const result: RollResult = {
          expression: roll.expression,
          total: roll.total,
          items: roll.detail?.items ?? [...roll.dice.map((d) => ({ kind: 'dice' as const, sign: 1 as const, faces: d.sides, results: [d.value] })), { kind: 'value' as const, sign: 1 as const, value: roll.modifier }],
          successes: roll.detail?.successes ?? null,
        };
        return result;
      }
      case 'chat.send': {
        if (!this.messages.take()) throw new HostError('rate-limited', say('mensagens demais em pouco tempo', 'too many messages in a short time'));
        const body = withImage(text(p.text, 2000).trim(), p.image);
        if (!body) return null;
        const kind = p.kind;
        if (kind === 'narrator') s.sendChat(body, 'chat', { kind: 'narrator' });
        else if (kind === 'npc') s.sendChat(body, 'chat', { kind: 'npc', name: text(p.name, 40) || 'NPC' });
        else if (kind === 'action' || kind === 'laugh') s.sendChat(body, kind);
        else s.sendChat(body, 'chat');
        return null;
      }
      case 'chat.local':
        s.addLocalLine(text(p.text, 2000));
        return null;
      case 'room.library':
        return this.library();
      case 'card.setBar':
      case 'card.setLine': {
        const index = Number(p.index);
        const slots = method === 'card.setBar' ? MEMBER_LIMITS.bars : MEMBER_LIMITS.lines;
        if (!Number.isInteger(index) || index < 0 || index >= slots) throw new HostError('bad-request', say(`índice inválido: use de 0 a ${slots - 1}`, `invalid index: use 0 to ${slots - 1}`));
        const userId = typeof p.userId === 'string' ? p.userId : this.opts.item.ownerId;
        if (!userId) throw new HostError('bad-request', say('esta tabela não tem dono: informe { userId }', 'this table has no owner: pass { userId }'));
        // todos com a ficha aberta rodam o mesmo código: só o dono do cartão e os mestres mexem nele,
        // e para os outros o pedido não faz nada (o servidor recusaria, com aviso de erro)
        if (userId !== s.user?.id && !currentNdbIdentity()?.owner) return null;
        if (method === 'card.setBar') {
          const value = Number(p.value);
          const max = Number(p.max);
          if (!Number.isFinite(value) || !Number.isFinite(max)) throw new HostError('bad-request', say('valor inválido', 'invalid value'));
          s.setCardBar(userId, index, Math.round(value), Math.round(max));
        } else {
          s.setCardLine(userId, index, text(p.text, 120));
        }
        return null;
      }
      case 'ui.toast':
        this.opts.ui.toast(text(p.text, 300), p.kind === 'success' || p.kind === 'error' ? p.kind : 'info');
        return null;
      case 'ui.confirm':
        return this.opts.ui.confirm({ title: text(p.title, 80) || this.opts.plugin.name, message: text(p.text, 1000) });
      case 'ui.prompt':
        return this.opts.ui.prompt({ title: this.opts.plugin.name, label: text(p.text, 300), initial: text(p.initial, 1000) });
      case 'ui.menu': {
        const items = Array.isArray(p.items) ? (p.items as MenuItem[]).slice(0, 50) : [];
        const rect = this.opts.iframe.getBoundingClientRect();
        return this.opts.ui.menu(items, { x: rect.left + Number(p.x || 0), y: rect.top + Number(p.y || 0) });
      }
      case 'ui.openItem': {
        const item = s.library.find((i) => i.id === p.id);
        if (!item || item.kind === 'dock' || item.kind === 'macros' || item.kind === 'folder') throw new HostError('not-found', say('item não encontrado', 'item not found'));
        s.requestOpenSheet(item.id);
        return null;
      }
      case 'ui.openLink': {
        const url = text(p.url, 2000);
        if (!/^https?:\/\//i.test(url)) throw new HostError('bad-request', say('só endereços http e https', 'only http and https addresses'));
        return this.opts.ui.openLink(url);
      }
      case 'ui.copy':
        await navigator.clipboard.writeText(text(p.text, 100_000));
        return null;
      case 'ui.download': {
        const data = p.data as string | Blob | ArrayBuffer;
        const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: text(p.mime, 100) || 'application/octet-stream' });
        if (blob.size > 20 * 1024 * 1024) throw new HostError('bad-request', say('arquivo grande demais', 'file too large'));
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = text(p.name, 120).replace(/[\\/:*?"<>|]/g, '_') || 'arquivo';
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
        return null;
      }
      case 'files.pickImage': {
        const picked = await this.opts.ui.pickImage();
        return picked ? { id: picked } : null;
      }
      case 'files.blob': {
        const id = text(p.id, 80);
        if (!/^[A-Za-z0-9_-]+$/.test(id)) throw new HostError('bad-request', say('arquivo inválido', 'invalid file'));
        // sem cache: o mesmo link pode já estar no `<img>` do chat (uma imagem que a ficha mandou), e
        // essa cópia veio sem os cabeçalhos de CORS que o `fetch` exige
        const response = await fetch(await fileUrl(id), { cache: 'no-store' });
        if (!response.ok) throw new HostError('not-found', say('arquivo não encontrado', 'file not found'));
        return await response.blob();
      }
      case 'storage.get':
        return this.storage()[text(p.key, 200)] ?? null;
      case 'storage.set': {
        const all = this.storage();
        const key = text(p.key, 200);
        if (p.value === null || p.value === undefined) delete all[key];
        else all[key] = p.value;
        const raw = JSON.stringify(all);
        if (raw.length > STORAGE_LIMIT) throw new HostError('bad-request', say('o armazenamento do plugin passa de 1 MB', 'the plugin storage is larger than 1 MB'));
        try {
          window.localStorage.setItem(this.storageKey(), raw);
        } catch {
          throw new HostError('failed', say('o navegador não deixou guardar', 'the browser did not allow saving'));
        }
        return null;
      }
      case 'storage.keys':
        return Object.keys(this.storage());
      case 'view.set': {
        // acima do teto, recusa: cortar o JSON no meio perderia a vista inteira ao reabrir
        const next = { ...this.view, [text(p.key, 200)]: p.value };
        const raw = JSON.stringify(next);
        if (raw.length > VIEW_LIMIT) throw new HostError('bad-request', say('a vista da aba passa de 100 KB', 'the tab view is larger than 100 KB'));
        this.view = next;
        try {
          window.sessionStorage.setItem(viewKey(this.opts.item.id), raw);
        } catch {
          // sem armazenamento: vale enquanto a aba estiver aberta
        }
        return null;
      }
      case 'webhooks.send': {
        if (!this.hooks.take()) throw new HostError('rate-limited', say('envios demais em pouco tempo', 'too many sends in a short time'));
        return await s.sendWebhook(this.opts.plugin.moduleId, text(p.name, 40), p.data);
      }
      default:
        throw new HostError('unsupported', say(`pedido desconhecido: ${String(method)}`, `unknown request: ${String(method)}`));
    }
  }

  private storageKey(): string {
    const me = useApp.getState().user?.id ?? 'anon';
    return `rpgdesk.webplugin.${me}.${this.opts.plugin.moduleId}`;
  }

  private storage(): Record<string, unknown> {
    try {
      const raw = window.localStorage.getItem(this.storageKey());
      return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
}
